import { useCallback } from "react";
import type { BaseEntity, SimpleTableConfig, FieldValue } from "../../types";
import { useFilterContext } from "@/contexts/FilterContext";
import { applyAdvancedFilter } from "../../utils";
import { createCustomRange } from "@/lib/utils/formatting/datetime-presets";

interface UseAdvancedFilterCallbacksProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
}

export function useAdvancedFilterCallbacks<TData extends BaseEntity>({
  config,
}: UseAdvancedFilterCallbacksProps<TData>) {
  const filterContext = useFilterContext<TData>();
  const { filters, actions: filterActions, uiState } = filterContext;

  const addAdvancedFilter = useCallback(
    (fieldId: string, filterType: string, value: FieldValue) => {
      const filtered = filters.advanced.filter((f) => (f.field || f.id) !== fieldId);
      filterActions.setAdvancedFilters([
        ...filtered,
        {
          id: fieldId,
          field: fieldId,
          operator: filterType,
          value,
        },
      ]);
    },
    [filters.advanced, filterActions]
  );

  const removeAdvancedFilter = useCallback(
    (fieldId: string) => {
      const newFilters = filters.advanced.filter((f) => f.id !== fieldId);
      filterActions.setAdvancedFilters(newFilters);
    },
    [filters.advanced, filterActions]
  );

  const clearAllAdvancedFilters = useCallback(() => {
    filterActions.setAdvancedFilters([]);
  }, [filterActions]);

  const handleCustomRangeClick = useCallback(
    (fieldId: string, fieldLabel: string) => {
      filterContext.actions.setCustomRangeModal({ isOpen: true, fieldId, fieldLabel });
    },
    [filterContext.actions]
  );

  const handleCustomRangeApply = useCallback(
    (start: Date, end: Date) => {
      const customRangeValue = createCustomRange(start, end);
      filterActions.setSimpleFilter(uiState.customRangeModal.fieldId, customRangeValue);
      filterContext.actions.setCustomRangeModal({ isOpen: false, fieldId: "", fieldLabel: "" });
    },
    [filterActions, uiState.customRangeModal.fieldId, filterContext.actions]
  );

  const handleCustomRangeClose = useCallback(() => {
    filterContext.actions.setCustomRangeModal({ isOpen: false, fieldId: "", fieldLabel: "" });
  }, [filterContext.actions]);

  const applyAdvancedFilterCallback = useCallback(
    (item: TData, fieldId: string, filter: { type: string; value: FieldValue }) => {
      return applyAdvancedFilter(item, fieldId, filter, config.fields);
    },
    [config.fields]
  );

  return {
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    applyAdvancedFilterCallback,
  };
}
